import { getUserError } from '../../../../components/miscUtils';
import { getDataTable } from '../../charts/chartDataBindings';
import { TpDashboardActionContext, TpDbActionStepDefinition as TpDbActionStepTypeDefinition } from '../interfaces';
import { addTableColumn, buildDerivedColumnPatch } from '../helpers/derivedColumnPatch';
import { createAddColumnAction, createColumnMetadata } from '../../../../data/mutation-ledger/schemas';
import type { AddColumnFromPropertyGroups } from '../../../../data/mutation-ledger/schemas';
import type { TpDataContext, TpDerivedColumnPatch } from '../../../../data/interfaces';

function getJsonSchema(): any {
  return {
    type: 'object',
    title: 'Add property group as column',
    description:
      'Add a column to a datatable based on the values of a row for a specific property group in another datatable, and store that result in a variable.',
    properties: {
      type: { type: 'string', const: 'AddPropertyGroupAsColumn' },
      id: {
        type: 'string',
        description: 'Identifier of the column to be created.',
      },
      propertyGroup: {
        type: 'string',
        description: 'Identifier of the property group.',
      },
      sourceTableId: {
        type: 'string',
        description: 'Identifier of the source data table.',
      },
      destDataType: {
        type: 'string',
        description:
          'Data type of the new column to be created. numerical, string, categorical, multi_categorical or multival_string',
      },
      destTableId: {
        type: 'string',
        description:
          'Identifier of the destination data table. Can be left empty if the source table is the same as the destination table.',
      },
      rowId: {
        type: 'string',
        description: 'Identifier of the row for which the value should be fetched',
      },
      newColDescription: {
        type: 'string',
        description: 'Description of the new column (defaults to the description of the property group).',
      },
    },
    required: ['type', 'id', 'propertyGroup', 'sourceTableId', 'destDataType', 'destTableId', 'rowId'],
  };
}

export function computePropertyGroupPatch(
  dataContext: TpDataContext,
  payload: AddColumnFromPropertyGroups & {
    targetTableId: string;
    targetColumnId: string;
  },
): TpDerivedColumnPatch {
  const sourceDataTable = getDataTable(dataContext, payload.sourceTableId);
  const matchingPropertyGroup = sourceDataTable.columnGroups.find((group) => group.id === payload.propertyGroup);
  if (!matchingPropertyGroup) throw getUserError(`Invalid property group: ${payload.propertyGroup}`);
  if (!(payload.rowId in sourceDataTable.itemIdMap))
    throw getUserError(`Row ${payload.rowId} not found in table ${payload.sourceTableId}`);

  const rowIdx = sourceDataTable.itemIdMap[payload.rowId];
  const colRowPairs: any = {};
  for (const colInfo of sourceDataTable.columns) {
    if (colInfo.groupId === matchingPropertyGroup.id)
      colRowPairs[colInfo.id] = sourceDataTable.columnData[colInfo.id][rowIdx];
  }
  const destTable = getDataTable(dataContext, payload.targetTableId);
  const newColData = Object.entries(destTable.itemIdMap)
    .sort((a, b) => a[1] - b[1])
    .map((entry) => (Object.hasOwnProperty.call(colRowPairs, entry[0]) ? colRowPairs[entry[0]] : null));
  const metadata = payload.columnMetadata;
  return buildDerivedColumnPatch(
    payload.targetTableId,
    payload.targetColumnId,
    metadata.name,
    metadata.description,
    metadata.valueType,
    metadata.groupId,
    metadata.groupName,
    metadata.groupDescription,
    newColData,
    metadata.decimalDigits,
  );
}

function execute(ctx: TpDashboardActionContext, step: any, onCompleted: () => void) {
  const sourceTableId = step.sourceTableId;

  const propertyGroup = step.propertyGroup;
  const rowId = step.rowId;

  const destTableId = step.destTableId ? step.destTableId : sourceTableId;

  const columnMetadata = createColumnMetadata({
    name: rowId,
    description: step.newColDescription,
    valueType: step.destDataType as string,
  });

  const payload: AddColumnFromPropertyGroups = {
    type: 'PROPERTY_GROUP_COLUMN',
    propertyGroup,
    sourceTableId,
    rowId,
    columnMetadata,
  };

  const ledgerAction = createAddColumnAction({
    targetTableId: destTableId,
    targetColumnIds: [step.id as string],
    payload,
  });

  const newColData = computePropertyGroupPatch(ctx.dataContext, {
    ...payload,
    targetTableId: destTableId,
    targetColumnId: step.id as string,
  }).columnData;
  addTableColumn(
    ctx,
    destTableId,
    step.id as string,
    step.rowId as string,
    step.newColDescription ?? null,
    step.destDataType as string,
    null,
    null,
    null,
    newColData,
    null,
    ledgerAction,
  );
  onCompleted();
}

const dbActionStepTypeAddPropertyGroupAsColumn: TpDbActionStepTypeDefinition = {
  stepType: 'AddPropertyGroupAsColumn',
  execute,
  getJsonSchema,
};

export default dbActionStepTypeAddPropertyGroupAsColumn;
